import { computed } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { map, Observable, of } from 'rxjs';

export type StorageType = 'session' | 'local';

export interface StorageOptions<T> {
  key: string;
  storageType?: StorageType;
  version?: number;
  ttl?: number;
  uid?: string | number;
  defaultValue?: T;
  deserialize?: (raw: string) => T;
  serialize?: (value: T) => string;
}

interface StorageEntry<T> {
  value: T;
  version?: number;
  expiresAt?: number;
  uid?: string | number;
}

export interface StorageState<T> {
  storageValue: T | null;
  storageLoaded: boolean;
  storageKey: string;
  uid?: string | number;
}

function resolveStorage(type: StorageType): Storage {
  return type === 'local' ? localStorage : sessionStorage;
}

function readFromStorage<T>(storage: Storage, key: string, options: StorageOptions<T>): T | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return options.defaultValue ?? null;

    const entry: StorageEntry<T> = JSON.parse(raw);

    if (
      (options.uid !== undefined && entry.uid !== options.uid) ||
      (options.version !== undefined && entry.version !== options.version) ||
      (entry.expiresAt !== undefined && Date.now() > entry.expiresAt)
    ) {
      storage.removeItem(key);
      return options.defaultValue ?? null;
    }

    return options.deserialize ? options.deserialize(JSON.stringify(entry.value)) : entry.value;
  } catch {
    return options.defaultValue ?? null;
  }
}

function writeToStorage<T>(
  storage: Storage,
  key: string,
  value: T,
  options: StorageOptions<T>,
): void {
  try {
    const serialized = options.serialize ? options.serialize(value) : JSON.stringify(value);

    let expiresAt: number | undefined;
    if (options.ttl !== undefined) {
      const raw = storage.getItem(key);
      if (raw) {
        try {
          const existing = JSON.parse(raw);
          if (typeof existing.expiresAt === 'number') {
            expiresAt = existing.expiresAt;
          }
        } catch {
          console.error('parsing ...');
        }
      }
      if (expiresAt === undefined) {
        expiresAt = Date.now() + options.ttl;
      }
    }

    const entry: StorageEntry<T> = {
      value: JSON.parse(serialized),
      ...(options.uid !== undefined && { uid: options.uid }),
      ...(options.version !== undefined && { version: options.version }),
      ...(expiresAt !== undefined && { expiresAt }),
    };

    storage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    console.warn(`[withStorage] write failed:`, e);
  }
}

/**
 * Feature Signal Store for `sessionStorage` and `localStorage`.
 *
 * @example
 * ```ts
 * export const UserStore = signalStore(
 *    withState({ user: null as User | null }),
 *    withStorage<User>({
 *      key: 'current_user',
 *      storageType: 'session',
 *      version: 1,
 *      ttl: 30 * 60 * 1000, // 30 min
 *    })
 * );
 * ```
 */
export function withStorage<T>(options: StorageOptions<T>) {
  const { key, storageType = 'session', defaultValue = null } = options;

  return signalStoreFeature(
    withState<StorageState<T>>({
      storageValue: defaultValue as T | null,
      storageLoaded: false,
      storageKey: key,
    }),

    withComputed((store) => ({
      hasStorageValue: computed(() => store.storageValue() !== null && store.storageLoaded()),
    })),

    withMethods((store) => {
      const storage = resolveStorage(storageType);

      return {
        loadFromStorage(): T | null {
          const value = readFromStorage<T>(storage, key, options);
          patchState(store, {
            storageValue: value,
            storageLoaded: true,
          });
          return value;
        },

        saveToStorage(value: T): void {
          writeToStorage<T>(storage, key, value, options);
          patchState(store, { storageValue: value });
        },

        clearStorage(): void {
          storage.removeItem(key);
          patchState(store, {
            storageValue: (defaultValue as T | null) ?? null,
            storageLoaded: false,
          });
        },

        peekStorage(): T | null {
          return readFromStorage<T>(storage, key, options);
        },

        patchStorageValue(partial: Partial<T>): void {
          const current = readFromStorage<T>(storage, key, options);
          const updated = { ...(current as object), ...partial } as T;
          writeToStorage<T>(storage, key, updated, options);
          patchState(store, { storageValue: updated });
        },

        loadOrFetch(fetcher: () => Observable<T | null>): Observable<T | null> {
          const cached = readFromStorage<T>(storage, key, options);

          if (cached !== null) {
            patchState(store, { storageValue: cached, storageLoaded: true });
            return of(cached);
          }

          return fetcher().pipe(
            map((fresh) => {
              if (fresh) {
                writeToStorage<T>(storage, key, fresh, options);
                patchState(store, { storageValue: fresh, storageLoaded: true });
              }
              return fresh;
            }),
          );
        },
      };
    }),
  );
}
